// =====================================================
// Error Handler Utility
// Digital Map - Victim Services Mapping Application
// =====================================================

import { AuthError, AuthErrorCode, AUTH_ERROR_CODES, ValidationError } from '../types/auth';

export class ErrorHandler {
  /**
   * Check whether a value is already a parsed AuthError object.
   */
  static isAuthError(error: any): error is AuthError {
    return (
      error &&
      typeof error === 'object' &&
      'code' in error &&
      Object.values(AUTH_ERROR_CODES).includes(error.code)
    );
  }

  /**
   * Parse API error response and convert to AuthError
   */
  static parseApiError(error: any): AuthError {
    // Network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return {
          code: AUTH_ERROR_CODES.CONNECTION_TIMEOUT,
          message: 'La connexion a expiré. Veuillez réessayer.',
          details: error
        };
      }

      // fetch() rejects with a TypeError on network-level failures. Browser
      // messages vary: Chrome "Failed to fetch", Safari "Load failed" / "cancelled",
      // Firefox "NetworkError when attempting to fetch resource."
      const message = (error.message || '').toLowerCase();
      if (
        error instanceof TypeError ||
        message.includes('network error') ||
        message.includes('fetch') ||
        message.includes('load failed') ||
        message.includes('networkerror') ||
        message.includes('network request failed') ||
        message.includes('cors')
      ) {
        return {
          code: AUTH_ERROR_CODES.NETWORK_ERROR,
          message: 'Erreur de connexion. Vérifiez votre connexion internet.',
          details: error
        };
      }

      return {
        code: AUTH_ERROR_CODES.UNKNOWN_ERROR,
        message: 'Une erreur inattendue s\'est produite.',
        details: error
      };
    }

    const { status, data } = error.response;

    // HTTP status code based errors
    switch (status) {
      case 400: {
        const fieldErrors = this.parseValidationErrors(data);
        const message = fieldErrors.length
          ? this.formatValidationErrors(fieldErrors)
          : data.message || 'Certaines informations sont incorrectes. Veuillez vérifier vos saisies.';
        return {
          code: AUTH_ERROR_CODES.VALIDATION_ERROR,
          message,
          details: data
        };
      }

      case 401: {
        const messageText = `${data.message || ''} ${data.error || ''}`.toLowerCase();
        if (
          messageText.includes('credentials') ||
          messageText.includes('password') ||
          messageText.includes('invalid')
        ) {
          return {
            code: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
            message: 'Email ou mot de passe incorrect.',
            details: data
          };
        }
        if (messageText.includes('locked')) {
          return {
            code: AUTH_ERROR_CODES.ACCOUNT_LOCKED,
            message: 'Votre compte est temporairement verrouillé.',
            details: data
          };
        }
        if (messageText.includes('disabled')) {
          return {
            code: AUTH_ERROR_CODES.ACCOUNT_DISABLED,
            message: 'Votre compte a été désactivé.',
            details: data
          };
        }
        if (messageText.includes('verified')) {
          return {
            code: AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED,
            message: 'Veuillez vérifier votre email avant de vous connecter.',
            details: data
          };
        }
        return {
          code: AUTH_ERROR_CODES.INVALID_CREDENTIALS,
          message: 'Email ou mot de passe incorrect.',
          details: data
        };
      }

      case 403:
        return {
          code: AUTH_ERROR_CODES.INSUFFICIENT_PERMISSIONS,
          message: 'Accès refusé. Permissions insuffisantes.',
          details: data
        };

      case 429:
        return {
          code: AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS,
          message: 'Trop de tentatives. Veuillez attendre avant de réessayer.',
          details: data
        };

      case 500:
        return {
          code: AUTH_ERROR_CODES.INTERNAL_ERROR,
          message: 'Erreur serveur. Veuillez réessayer plus tard.',
          details: data
        };

      default:
        return {
          code: AUTH_ERROR_CODES.UNKNOWN_ERROR,
          message: data.message || 'Une erreur inattendue s\'est produite.',
          details: data
        };
    }
  }

  /**
   * Get user-friendly, actionable error message
   */
  /**
   * Human-readable wait duration in French, e.g. "15 minutes" or "1 heure et 5 minutes".
   */
  static formatRetryDelay(totalMinutes: number): string {
    const mins = Math.max(1, Math.ceil(totalMinutes));
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const rest = mins % 60;
      const h = `${hours} heure${hours > 1 ? 's' : ''}`;
      return rest > 0 ? `${h} et ${rest} minute${rest > 1 ? 's' : ''}` : h;
    }
    return `${mins} minute${mins > 1 ? 's' : ''}`;
  }

  static getErrorMessage(error: AuthError): string {
    const retryAfter = this.getRetryAfterMinutes(error);
    const retryDelay = retryAfter != null ? this.formatRetryDelay(retryAfter) : null;
    const retrySuffix = retryDelay ? ` Réessayez dans ${retryDelay}.` : '';

    const messages: Record<AuthErrorCode, string> = {
      [AUTH_ERROR_CODES.NETWORK_ERROR]:
        'Connexion impossible. Vérifiez votre accès internet, puis réessayez.',
      [AUTH_ERROR_CODES.CONNECTION_TIMEOUT]:
        'Le serveur met trop de temps à répondre. Vérifiez votre connexion et réessayez.',
      [AUTH_ERROR_CODES.SERVER_UNAVAILABLE]:
        'Le service est momentanément indisponible. Veuillez réessayer dans quelques instants.',
      [AUTH_ERROR_CODES.INVALID_CREDENTIALS]:
        "L'email ou le mot de passe est incorrect. Vérifiez vos informations ou réinitialisez votre mot de passe.",
      [AUTH_ERROR_CODES.ACCOUNT_LOCKED]:
        `Votre compte est temporairement verrouillé après plusieurs tentatives échouées.${retrySuffix} Contactez le support si le problème persiste.`,
      [AUTH_ERROR_CODES.ACCOUNT_DISABLED]:
        'Votre compte a été désactivé. Contactez le support pour plus d\'informations.',
      [AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED]:
        'Votre adresse email n\'a pas été confirmée. Vérifiez votre boîte de réception (et vos spams) ou renvoyez l\'email de vérification.',
      [AUTH_ERROR_CODES.INSUFFICIENT_PERMISSIONS]:
        'Vous n\'avez pas les droits nécessaires pour accéder à cette page.',
      [AUTH_ERROR_CODES.ROLE_REQUIRED]: 'Accès restreint à certains profils.',
      [AUTH_ERROR_CODES.PERMISSION_DENIED]: 'Cette action ne vous est pas autorisée.',
      [AUTH_ERROR_CODES.VALIDATION_ERROR]:
        'Certaines informations sont incorrectes. Vérifiez les champs indiqués ci-dessous.',
      [AUTH_ERROR_CODES.REQUIRED_FIELD]: 'Ce champ est obligatoire.',
      [AUTH_ERROR_CODES.INVALID_EMAIL]:
        'Le format de l\'email est invalide (ex. : nom@exemple.fr).',
      [AUTH_ERROR_CODES.WEAK_PASSWORD]:
        'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.',
      [AUTH_ERROR_CODES.PASSWORD_MISMATCH]:
        'Les mots de passe ne correspondent pas. Saisissez le même mot de passe deux fois.',
      [AUTH_ERROR_CODES.SESSION_EXPIRED]:
        'Votre session a expiré pour des raisons de sécurité. Veuillez vous reconnecter.',
      [AUTH_ERROR_CODES.TOKEN_INVALID]:
        'Ce lien n\'est pas valide. Demandez-en un nouveau si nécessaire.',
      [AUTH_ERROR_CODES.TOKEN_EXPIRED]:
        'Ce lien a expiré. Demandez-en un nouveau pour recommencer.',
      [AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS]: retryDelay
        ? `Trop de tentatives ont été effectuées. Veuillez réessayer dans ${retryDelay}.`
        : 'Trop de tentatives ont été effectuées. Veuillez patienter avant de réessayer.',
      [AUTH_ERROR_CODES.RATE_LIMIT_EXCEEDED]: retryDelay
        ? `La limite de requêtes a été atteinte. Veuillez réessayer dans ${retryDelay}.`
        : 'La limite de requêtes a été atteinte. Veuillez patienter avant de réessayer.',
      [AUTH_ERROR_CODES.INTERNAL_ERROR]:
        'Une erreur technique est survenue. Veuillez réessayer dans quelques instants.',
      [AUTH_ERROR_CODES.DATABASE_ERROR]:
        'Le service est momentanément indisponible. Veuillez réessayer plus tard.',
      [AUTH_ERROR_CODES.UNKNOWN_ERROR]:
        'Une erreur inattendue s\'est produite. Veuillez réessayer.'
    };

    return messages[error.code] || error.message || messages[AUTH_ERROR_CODES.UNKNOWN_ERROR];
  }

  /**
   * Extract a user-facing summary of API validation errors.
   */
  static formatValidationErrors(errors: ValidationError[]): string {
    if (!errors.length) {
      return 'Certaines informations sont incorrectes. Vérifiez les champs indiqués ci-dessous.';
    }
    return errors.map(e => e.message).join(' ');
  }

  /**
   * Try to read a retry-after hint (in minutes) from the error details.
   * Accepts minute-based fields (retryAfter, retryAfterMinutes, ...) as well
   * as second-based ones (retryAfterSeconds, e.g. from the Retry-After header).
   */
  static getRetryAfterSeconds(error: AuthError): number | null {
    const details = error.details;
    if (!details || typeof details !== 'object') return null;

    const secondCandidates = [
      details.retryAfterSeconds,
      details.retryAfter,
    ];
    for (const value of secondCandidates) {
      const parsed = typeof value === 'number' ? value : parseInt(value, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return Math.ceil(parsed);
      }
    }

    const minuteCandidates = [
      details.retryAfterMinutes,
      details.lockoutMinutes,
      details.waitMinutes,
    ];
    for (const value of minuteCandidates) {
      if (typeof value === 'number' && value > 0) {
        return Math.ceil(value * 60);
      }
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed) && parsed > 0) return parsed * 60;
      }
    }

    return null;
  }

  static getRetryAfterMinutes(error: AuthError): number | null {
    const seconds = this.getRetryAfterSeconds(error);
    return seconds != null ? Math.max(1, Math.ceil(seconds / 60)) : null;
  }

  /**
   * Check if error is retryable
   */
  static isRetryable(error: AuthError): boolean {
    const retryableErrors = [
      AUTH_ERROR_CODES.NETWORK_ERROR,
      AUTH_ERROR_CODES.CONNECTION_TIMEOUT,
      AUTH_ERROR_CODES.SERVER_UNAVAILABLE,
      AUTH_ERROR_CODES.INTERNAL_ERROR
    ];

    return retryableErrors.includes(error.code as any);
  }

  /**
   * Get retry delay in milliseconds
   */
  static getRetryDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s
    return Math.min(1000 * Math.pow(2, attempt), 16000);
  }

  /**
   * Parse validation errors from API response
   */
  static parseValidationErrors(data: any): ValidationError[] {
    if (!data.errors || !Array.isArray(data.errors)) {
      return [];
    }

    return data.errors.map((error: any) => ({
      field: error.field || 'unknown',
      message: error.message || 'Erreur de validation'
    }));
  }

  /**
   * Create a generic error
   */
  static createError(code: AuthErrorCode, message: string, details?: any): AuthError {
    return {
      code,
      message,
      details
    };
  }
}
